import { BtnPrimary } from '@components/buttons';
import { useIntl } from 'shared/intl/use-intl';
import { TRACKER_LINK_CLASS, TrackerGlance, trackerHref } from './glance';

/**
 * The course Home's "Today's touches": up to 5 people who are overdue or due
 * today. Log touch opens the Tracker tab with that person's panel open, so
 * logging lives in one place.
 */
export function TodaysTouchesCard({
  glance,
  courseUrl,
}: {
  glance: TrackerGlance;
  courseUrl: string;
}) {
  const intl = useIntl();
  function withTabLink([before, link, after]: string[]) {
    return (
      <p class="text-sm text-gray-700 dark:text-gray-300">
        {before}
        <a class={TRACKER_LINK_CLASS} href={trackerHref(courseUrl)}>
          {link}
        </a>
        {after}
      </p>
    );
  }
  return (
    <section class="flex flex-col gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-4">
      <header class="flex items-center justify-between gap-3">
        <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">
          {intl("Today's touches")}
        </h2>
        {glance.dueCount > 0 && (
          <span class="text-sm text-gray-600 dark:text-gray-400 shrink-0">
            {intl('{count:number} due', { count: glance.dueCount })}
          </span>
        )}
      </header>
      {!glance.hasPeople &&
        withTabLink(intl.split("Add the people you're contacting on the <>Tracker tab</>."))}
      {glance.hasPeople &&
        !glance.due.length &&
        withTabLink(
          intl.split('Nothing due today. Add people or log touches on the <>Tracker tab</>.'),
        )}
      {glance.due.length > 0 && (
        <ul class="flex flex-col">
          {glance.due.map((person) => (
            <li
              key={person.id}
              class="flex items-center gap-3 min-h-14 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
            >
              <span class="grow min-w-0 flex flex-col">
                <span
                  class="font-semibold text-gray-900 dark:text-gray-100 truncate"
                  title={person.name}
                >
                  {person.name}
                </span>
                {person.nextText && (
                  <span
                    class={`text-sm truncate ${
                      person.isLate
                        ? 'text-red-700 dark:text-red-400'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                    title={person.nextText}
                  >
                    {person.nextText}
                  </span>
                )}
              </span>
              <BtnPrimary
                class="shrink-0 inline-flex items-center justify-center min-h-11 min-w-11"
                href={trackerHref(courseUrl, person.id)}
              >
                {intl('Log touch')}
              </BtnPrimary>
            </li>
          ))}
        </ul>
      )}
      {glance.dueCount > glance.due.length && (
        <a class={`self-start text-sm ${TRACKER_LINK_CLASS}`} href={trackerHref(courseUrl)}>
          {intl('See all ({count:number})', { count: glance.dueCount })}
        </a>
      )}
    </section>
  );
}
